import { Button, Card, Descriptions, Modal, message, Spin, Table } from "antd";
import React, { Fragment } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import entitlements from "../config/entitlements";
import { Category, SubCategory } from "../helpers/shared-interfaces";
import { convertSlaToDays, request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import { DELETE_CATEGORY, GET_CATEGORY } from "../gql/categories";

interface RouteParams {
  id: string;
}

interface State {
  name: string;
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  category?: Category;
  subCategories?: SubCategory[];
}

export const getSubCategoriesColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render(text: string, record: SubCategory): JSX.Element {
      return (
        <NavLink to={`/subCategories/${record?.id}`}>{record?.name}</NavLink>
      );
    },
  },
  {
    title: "SLA (Days)",
    dataIndex: "sla",
    key: "sla",
    render(text: string, record: SubCategory): JSX.Element {
      return <span>{convertSlaToDays(record.sla)}</span>;
    },
  },
];

function ModalWrapper(
  showDialog: boolean,
  deleteCategory: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteCategory}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this category?</div>
    </Modal>
  );
}

export default class ViewCategory extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      name: "",
      isLoading: true,
      id: "",
      showDialog: false,
      category: undefined,
      subCategories: [],
    };
    this.deleteCategory = this.deleteCategory.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getCategory();
    });
  }

  getCategory = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_CATEGORY,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              category: data.data.category,
              subCategories: data.data.category?.subCategories,
            });
          } else {
            message.error("Failed to get category data");
          }
        });
      },
    );
  };

  deleteCategory(): void {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_CATEGORY,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteCategory.result) {
              message.success("Category deleted successfuly");
              this.redirectToAllCategoriesPage();
            } else {
              message.error("Failed to delete category");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllCategoriesPage(): void {
    this.props.history.push("/categories");
  }
  render(): JSX.Element | null {
    const { category, isLoading, id, showDialog, subCategories } = this.state;

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteCategory,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                title={<div>{category?.name}</div>}
                extra={
                  <div>
                    <EntitledSection entitlement={entitlements.Products.edit}>
                      <NavLink to={`/categories/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "  rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-category-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection entitlement={entitlements.Products.delete}>
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-category-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                  </div>
                }
              >
                <Descriptions.Item label={<b>Name</b>} key="Name">
                  {category?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Department</b>} key="Department">
                  {category?.department?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Is Active</b>} key="Is Activeame">
                  {category?.isActive.toString()}
                </Descriptions.Item>
              </Descriptions>
            </div>
            {subCategories && (
              <div>
                <p
                  style={{
                    marginTop: "20px",
                    fontSize: "15px",
                  }}
                >
                  <b>Subcategories</b>
                </p>
                <Table
                  scroll={{ x: 400 }}
                  columns={getSubCategoriesColumns}
                  dataSource={subCategories}
                />
              </div>
            )}
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
